// Gestão de pastas, dentro da barra lateral.
//
// Fica aqui e não numa tela de configurações porque é onde o usuário está
// quando percebe que precisa de uma pasta: olhando a lista de mensagens.

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as correio from "../api/correio";
import { ErroDaAPI, explicar } from "../api/problema";
import { Icone } from "../ui/Icone";

/** As do sistema não se renomeiam nem se apagam — o servidor também recusa. */
const DO_SISTEMA = new Set(["INBOX", "Sent", "Drafts", "Trash", "Junk", "Archive"]);

export function podeMexer(nome: string | undefined): boolean {
  return !!nome && !DO_SISTEMA.has(nome);
}

/**
 * Para onde dá para mover, estando na pasta `atual`.
 *
 * Função pura e exportada para ter teste: a suíte deste projeto roda em
 * `environment: "node"` e cobre LÓGICA, não componente. Deixar esta regra
 * dentro do JSX seria deixá-la sem teste nenhum.
 *
 * A PASTA ATUAL SAI DA LISTA porque mover para onde já se está não é ação —
 * e oferecê-la faria o usuário clicar, esperar e não ver nada acontecer.
 *
 * Pasta sem nome não entra: o servidor não devolve isso hoje, mas um `nome`
 * ausente viraria uma linha em branco clicável que move para lugar nenhum.
 */
export function destinosPossiveis(
  pastas: { nome?: string }[] | undefined,
  atual: string,
): string[] {
  return (pastas ?? [])
    .map((p) => p.nome)
    .filter((n): n is string => !!n && n !== atual);
}

export function GerirPastas({ pastas }: { pastas: { nome?: string }[] }) {
  const fila = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [nova, setNova] = useState("");
  const [erro, setErro] = useState("");

  const recarregar = () => {
    void fila.invalidateQueries({ queryKey: ["pastas"] });
    void fila.invalidateQueries({ queryKey: ["mensagens"] });
  };

  const criar = useMutation({
    mutationFn: () => correio.criarPasta(nova.trim()),
    onSuccess: () => {
      setNova("");
      setErro("");
      setAberto(false);
      recarregar();
    },
    onError: (e) => setErro(primeiraMensagem(e)),
  });

  return (
    <div className="px-3 pb-2">
      {!aberto ? (
        <button
          onClick={() => setAberto(true)}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-tinta-fraca hover:bg-linha"
        >
          <Icone nome="Plus" tamanho={16} />
          Nova pasta
        </button>
      ) : (
        <div className="rounded-lg border border-linha bg-white p-2">
          <input
            value={nova}
            onChange={(e) => setNova(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && nova.trim()) criar.mutate();
              if (e.key === "Escape") setAberto(false);
            }}
            placeholder="Nome da pasta"
            aria-label="Nome da nova pasta"
            autoFocus
            className="w-full rounded border border-linha px-2 py-1 text-sm"
          />
          {erro && <p className="mt-1 text-xs text-perigo">{erro}</p>}
          <div className="mt-2 flex gap-2">
            <button
              onClick={() => criar.mutate()}
              disabled={!nova.trim() || criar.isPending}
              className="rounded bg-acao px-3 py-1 text-xs text-white disabled:opacity-60"
            >
              Criar
            </button>
            <button
              onClick={() => {
                setAberto(false);
                setErro("");
              }}
              className="rounded px-3 py-1 text-xs text-tinta-fraca"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
      {/* `pastas` entra só para a contagem: sem nenhuma própria, o botão de
          criar é a única coisa útil aqui. */}
      {pastas.length === 0 && null}
    </div>
  );
}

export function AcoesDaPasta({ nome, aoMudar }: { nome: string; aoMudar: () => void }) {
  const [renomeando, setRenomeando] = useState(false);
  const [novoNome, setNovoNome] = useState(nome);
  const [erro, setErro] = useState("");

  const renomear = useMutation({
    mutationFn: () => correio.renomearPasta(nome, novoNome.trim()),
    onSuccess: () => {
      setRenomeando(false);
      setErro("");
      aoMudar();
    },
    onError: (e) => setErro(primeiraMensagem(e)),
  });

  const apagar = useMutation({
    mutationFn: (confirmado: boolean) => correio.apagarPasta(nome, confirmado),
    onSuccess: () => aoMudar(),
    onError: (e) => {
      // 409 com mensagens dentro: o servidor recusou de propósito para a
      // interface poder perguntar. Perguntar aqui, e não antes, evita um
      // diálogo em pasta vazia.
      if (e instanceof ErroDaAPI && e.status === 409 && !e.mensagem.includes("sistema")) {
        if (
          window.confirm(
            `A pasta "${nome}" tem mensagens dentro.\n\n` +
              "Apagar a pasta apaga as mensagens junto. Continuar?",
          )
        ) {
          apagar.mutate(true);
          return;
        }
        return;
      }
      setErro(primeiraMensagem(e));
    },
  });

  if (renomeando) {
    return (
      <div className="px-3 py-2">
        <input
          value={novoNome}
          onChange={(e) => setNovoNome(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") renomear.mutate();
            if (e.key === "Escape") setRenomeando(false);
          }}
          aria-label={`Novo nome para ${nome}`}
          autoFocus
          className="w-full rounded border border-linha px-2 py-1 text-sm"
        />
        {erro && <p className="mt-1 text-xs text-perigo">{erro}</p>}
      </div>
    );
  }

  return (
    <span className="flex items-center gap-0.5">
      <button
        onClick={(e) => {
          e.preventDefault();
          setRenomeando(true);
        }}
        aria-label={`Renomear ${nome}`}
        title="Renomear"
        className="rounded p-1 text-tinta-fraca hover:bg-white"
      >
        <Icone nome="Pencil" tamanho={12} />
      </button>
      <button
        onClick={(e) => {
          e.preventDefault();
          apagar.mutate(false);
        }}
        aria-label={`Apagar ${nome}`}
        title="Apagar"
        className="rounded p-1 text-perigo hover:bg-white"
      >
        <Icone nome="Trash2" tamanho={12} />
      </button>
    </span>
  );
}

/** A mensagem do campo quando existe; senão a geral. */
function primeiraMensagem(e: unknown): string {
  if (e instanceof ErroDaAPI) {
    const doCampo = Object.values(e.porCampo)[0]?.[0];
    if (doCampo) return doCampo;
  }
  return explicar(e);
}

/**
 * O menu de mover UMA mensagem para uma pasta.
 *
 * POR QUE EXISTE: a barra da leitura tinha "Arquivar", "Spam" e "Excluir" —
 * três destinos FIXOS — e nenhuma forma de pôr a mensagem numa pasta do
 * próprio usuário. Quem tentava acabava no menu de marcadores, que é outra
 * coisa: marcador é keyword do IMAP e NÃO MOVE NADA.
 *
 * Medido em 30/10/2026: o Jhou tinha uma PASTA `jhou`, foi procurá-la no
 * seletor de marcador e não achou. A pasta estava na barra lateral o tempo
 * todo — só não havia por onde mandar a mensagem para ela.
 */
export function MenuDePastas({
  atual,
  aoEscolher,
  desabilitado,
}: {
  atual: string;
  aoEscolher: (nome: string) => void;
  desabilitado?: boolean;
}) {
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function fora(e: MouseEvent) {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    }
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  // A MESMA CHAVE DA BARRA LATERAL: as pastas já estão em cache quando esta
  // tela abre, então o menu não espera rede.
  const { data } = useQuery({ queryKey: ["pastas"], queryFn: () => correio.pastas() });
  const destinos = destinosPossiveis(data ? correio.ordenar(data.pastas) : [], atual);

  return (
    <div className="relative" ref={caixa}>
      <button
        onClick={() => setAberto((a) => !a)}
        aria-label="Mover para pasta"
        title="Mover para pasta"
        aria-haspopup="menu"
        aria-expanded={aberto}
        disabled={desabilitado || destinos.length === 0}
        className="rounded-full p-2 hover:bg-fundo disabled:opacity-50"
      >
        <Icone nome="Folder" tamanho={18} />
      </button>

      {aberto && (
        <div
          role="menu"
          // left-0 pelo mesmo motivo do menu de marcadores: alinhar pela
          // direita joga o menu por baixo da barra lateral, cortado.
          //
          // E A LARGURA É LIMITADA À TELA. O container que envolve a lista
          // tem `overflow-hidden`, então um menu de 240px ancorado num botão
          // perto da borda direita é CORTADO em tela estreita — que foi onde
          // a Emilia viu o problema em 30/10/2026. Com o teto, ele encolhe em
          // vez de sumir pela borda.
          className="absolute left-0 z-30 mt-1 w-60 max-w-[calc(100vw-2rem)] rounded-xl border border-linha bg-white py-1 shadow-lg"
        >
          {destinos.map((n) => (
            <button
              key={n}
              role="menuitem"
              onClick={() => {
                setAberto(false);
                aoEscolher(n);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-fundo"
            >
              <Icone nome="Folder" tamanho={14} className="text-tinta-media" />
              <span className="truncate">{n}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
